/**
 * Subscription verification polling utility.
 *
 * This utility polls the backend to verify that a subscription has been activated
 * after a Stripe checkout. It accounts for webhook processing delays and ensures
 * the frontend doesn't proceed until the subscription is confirmed in both
 * Cognito and DynamoDB.
 */

import { backendApiService } from "../services/backendApiService";

export interface SubscriptionVerificationResult {
  verified: boolean;
  status: string;
  tier?: string;
  source: string;
  has_active_subscription: boolean;
}

export interface PollingOptions {
  /** Maximum time to poll in milliseconds (default: 30000 - 30 seconds) */
  maxDuration?: number;
  /** Interval between poll attempts in milliseconds (default: 2000 - 2 seconds) */
  interval?: number;
  /** Callback for each poll attempt */
  onPollAttempt?: (attempt: number, result?: SubscriptionVerificationResult) => void;
  /** Callback when polling times out */
  onTimeout?: () => void;
}

/**
 * Poll the subscription verification endpoint until the subscription is confirmed
 * or the timeout is reached.
 *
 * @param options - Polling configuration options
 * @returns Promise that resolves with the verification result or rejects on timeout
 *
 * @example
 * ```typescript
 * try {
 *   const result = await pollForSubscriptionVerification({
 *     maxDuration: 30000, // 30 seconds
 *     onPollAttempt: (attempt) => console.log(`Attempt ${attempt}...`)
 *   });
 *   if (result.verified) {
 *     // Subscription confirmed!
 *     navigate('/trees');
 *   }
 * } catch (error) {
 *   // Timeout or other error
 *   console.error('Failed to verify subscription:', error);
 * }
 * ```
 */
export async function pollForSubscriptionVerification(
  options: PollingOptions = {}
): Promise<SubscriptionVerificationResult> {
  const {
    maxDuration = 30000, // 30 seconds
    interval = 2000, // 2 seconds
    onPollAttempt,
    onTimeout,
  } = options;

  const startTime = Date.now();
  let attempt = 0;

  return new Promise((resolve, reject) => {
    const poll = async () => {
      attempt++;
      const elapsed = Date.now() - startTime;

      // Check if we've exceeded the maximum duration
      if (elapsed >= maxDuration) {
        onTimeout?.();
        reject(
          new Error(
            "Subscription verification timed out after " + Math.floor(maxDuration / 1000) + " seconds"
          )
        );
        return;
      }

      try {
        const result = await backendApiService.verifySubscription();

        // Notify caller of poll attempt
        onPollAttempt?.(attempt, result);

        // Check if subscription is verified
        if (result.verified && result.has_active_subscription) {
          resolve(result);
          return;
        }

        // Schedule next poll
        setTimeout(poll, interval);
      } catch (error) {
        console.error("Error during subscription verification poll:", error);

        // If it's an auth error, stop polling
        if ((error as any)?.response?.status === 401) {
          reject(new Error("Authentication required for subscription verification"));
          return;
        }

        // Notify caller of poll attempt (with no result due to error)
        onPollAttempt?.(attempt);

        // Schedule next poll despite error (webhook might still be processing)
        setTimeout(poll, interval);
      }
    };

    // Start polling
    poll();
  });
}

/**
 * Helper function to poll with visual feedback.
 * Returns a cleanup function to stop polling if component unmounts.
 *
 * @param onSuccess - Callback when subscription is verified
 * @param onError - Callback when polling fails or times out
 * @param onProgress - Callback for progress updates
 * @returns Cleanup function to cancel polling
 */
export function startSubscriptionPolling(
  onSuccess: (result: SubscriptionVerificationResult) => void,
  onError: (error: Error) => void,
  onProgress?: (message: string, attempt: number) => void
): () => void {
  let cancelled = false;

  pollForSubscriptionVerification({
    maxDuration: 30000,
    interval: 2000,
    onPollAttempt: (attempt, result) => {
      if (cancelled) return;

      if (result) {
        onProgress?.(
          `Verifying subscription... (attempt ${attempt}, status: ${result.status})`,
          attempt
        );
      } else {
        onProgress?.(
          `Checking subscription status... (attempt ${attempt})`,
          attempt
        );
      }
    },
    onTimeout: () => {
      if (cancelled) return;
      onProgress?.(
        "Subscription verification timed out. Please check your subscription page.",
        0
      );
    },
  })
    .then((result) => {
      if (!cancelled) {
        onSuccess(result);
      }
    })
    .catch((error) => {
      if (!cancelled) {
        onError(error);
      }
    });

  // Return cleanup function
  return () => {
    cancelled = true;
  };
}
